'use client'

import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { MiniRuleData } from '@/src/types/flight'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'
import { FareRuleSkeleton } from '../../../desktop/srp/flightResults/flightDetails/fareRulesSkeleton'
import NoFareRules from '../../../desktop/srp/flightResults/flightDetails/noFareRules'
import NoteSection from '../../../desktop/srp/flightResults/flightDetails/noteSection'
import RefundInfo from '../../../desktop/srp/flightResults/flightDetails/refundInfo'
import FareRulesTable from './fareRulesTable'

interface FareRuleSectionProps {
    miniruleData: MiniRuleData | undefined
    loading: boolean
    error: string | null
}

const FareRuleSection: React.FC<FareRuleSectionProps> = ({ miniruleData, loading, error }) => {
    const { formatCurrency } = useFormatCurrencyForSrp()
    const t = useTranslations('srpFlightDetails')

    if (loading) return <FareRuleSkeleton />
    if (error)
        return (
            <Typography color="error" p={2}>
                {t('fareRulesError')}
            </Typography>
        )
    if (!miniruleData) return <Typography p={2}>{t('noFareRuleData')}</Typography>

    const showFareRules = miniruleData.MR === 'Y' && miniruleData.Refund === 'Y'
    const airlineCancellationFee = miniruleData.ADT?.BD?.C ? formatCurrency(Number(miniruleData.ADT.BD.C)) : 'N/A'
    const airlineDateChangeFee = miniruleData.ADT?.BD?.A ? formatCurrency(Number(miniruleData.ADT.BD.A)) : 'N/A'
    const flyinCancellationFee = miniruleData.ctc ? formatCurrency(Number(miniruleData.ctc)) : 'N/A'
    const flyinDateChangeFee = miniruleData.cta ? formatCurrency(Number(miniruleData.cta)) : 'N/A'
    const noteText = miniruleData.note

    return (
        <Box
            sx={{
                p: { md: 2 },
                pt: { xs: 2, md: 0 }
            }}
        >
            <Typography variant="body1" fontWeight={700} sx={{ mb: 2, textAlign: 'start' }}>
                {t('baseFarePerTraveler')}
            </Typography>

            {showFareRules ? (
                <FareRulesTable
                    airlineCancellationFee={airlineCancellationFee}
                    flyinCancellationFee={flyinCancellationFee}
                    airlineDateChangeFee={airlineDateChangeFee}
                    flyinDateChangeFee={flyinDateChangeFee}
                />
            ) : miniruleData.MR === 'N' || miniruleData.Refund === 'N' ? (
                <RefundInfo />
            ) : (
                <NoFareRules />
            )}

            {noteText && <NoteSection />}
        </Box>
    )
}

export default FareRuleSection
